/** Where the events of a voice begin, with the time grace notes take from their neighbours. */

import { PPQ, ticksOf } from "../document/duration";
import type { MusicEvent, Voice } from "../document/types";

/** The longest a single grace note is allowed to take from the note it leans on. */
const GRACE_TICKS = PPQ / 4;

/**
 * Where each event of a voice begins, in ticks, with grace notes taken into account.
 *
 * A grace note occupies no time in the bar, so it shares an onset with the note after it.
 * Played that way both would sound at once, which is not what the notation means: the
 * grace is heard before the beat's note and shortens it. Each grace takes up to a
 * semiquaver, and never more than half of what it leans on, so a run of them before a
 * short note stays inside it.
 *
 * Graces at the end of a bar lean on the first note of the *next* bar, which this function
 * cannot see. They are placed so that the run ends at the bar line instead, which is where
 * they are heard, rather than being left with no length at all and never sounding.
 */
export function onsets(
  voice: Voice,
): readonly { event: MusicEvent; start: number; ticks: number }[] {
  const placed: { event: MusicEvent; start: number; ticks: number }[] = [];
  let clock = 0;
  let graces: number[] = [];

  const spread = (from: number, each: number): void => {
    graces.forEach((at, order) => {
      const entry = placed[at];
      if (entry !== undefined) placed[at] = { ...entry, start: from + order * each, ticks: each };
    });
  };

  for (const event of voice.events) {
    const isGrace = event.kind === "note" && event.grace !== null;
    if (isGrace) {
      graces.push(placed.length);
      placed.push({ event, start: clock, ticks: 0 });
      continue;
    }

    const ticks = ticksOf(event.duration);
    if (graces.length > 0 && ticks > 0) {
      const stolen = Math.min(GRACE_TICKS * graces.length, ticks / 2);
      spread(clock, stolen / graces.length);
      placed.push({ event, start: clock + stolen, ticks: ticks - stolen });
    } else {
      placed.push({ event, start: clock, ticks });
    }
    clock += ticks;
    graces = [];
  }

  if (graces.length > 0) {
    const taken = Math.min(GRACE_TICKS * graces.length, Math.max(clock / 2, GRACE_TICKS));
    spread(Math.max(clock - taken, 0), taken / graces.length);
  }

  return placed;
}
